import { useMutation } from "@tanstack/react-query";
import { gqlReqNext } from "~utils/api/gql-req/next";
import type {
	ActivateManagedAccountToPaidInput,
	PreviewManagedAccountSubscriptionChangeInput,
	UpdateManagedAccountSubscriptionInput,
} from "~mangomap-ui/templates/partner-templates/types";

const previewMutation = /* GraphQL */ `
	mutation PreviewManagedAccountSubscriptionChange(
		$input: PreviewManagedAccountSubscriptionChangeInput!
	) {
		previewManagedAccountSubscriptionChange(input: $input) {
			chargeTotal
			creditTotal
		}
	}
`;

const activateMutation = /* GraphQL */ `
	mutation ActivateManagedAccountToPaid(
		$input: ActivateManagedAccountToPaidInput!
	) {
		activateManagedAccountToPaid(input: $input) {
			status
			threeDSecureActionTokenId
			account {
				id
			}
		}
	}
`;

const updateMutation = /* GraphQL */ `
	mutation UpdateManagedAccountSubscription(
		$input: UpdateManagedAccountSubscriptionInput!
	) {
		updateManagedAccountSubscription(input: $input) {
			status
			threeDSecureActionTokenId
			account {
				id
			}
		}
	}
`;

const cancelMutation = /* GraphQL */ `
	mutation CancelManagedAccountSubscription($accountId: ID!) {
		cancelManagedAccountSubscription(accountId: $accountId) {
			id
		}
	}
`;

const endTrialMutation = /* GraphQL */ `
	mutation EndManagedAccountTrialEarly($accountId: ID!) {
		endManagedAccountTrialEarly(accountId: $accountId) {
			id
		}
	}
`;

export const usePreviewManagedAccountSubscriptionChange = () =>
	useMutation({
		mutationFn: (input: PreviewManagedAccountSubscriptionChangeInput) =>
			gqlReqNext<
				{
					previewManagedAccountSubscriptionChange: {
						chargeTotal: number | null;
						creditTotal: number | null;
					};
				},
				{ input: PreviewManagedAccountSubscriptionChangeInput }
			>({
				query: previewMutation,
				variables: { input },
			}),
	});

export const useActivateManagedAccountToPaid = () =>
	useMutation({
		mutationFn: (input: ActivateManagedAccountToPaidInput) =>
			gqlReqNext({
				query: activateMutation,
				variables: { input },
			}),
	});

export const useUpdateManagedAccountSubscription = () =>
	useMutation({
		mutationFn: (input: UpdateManagedAccountSubscriptionInput) =>
			gqlReqNext({
				query: updateMutation,
				variables: { input },
			}),
	});

export const useCancelManagedAccountSubscription = () =>
	useMutation({
		mutationFn: (accountId: string) =>
			gqlReqNext({ query: cancelMutation, variables: { accountId } }),
	});

export const useEndManagedAccountTrialEarly = () =>
	useMutation({
		mutationFn: (accountId: string) =>
			gqlReqNext({ query: endTrialMutation, variables: { accountId } }),
	});

const restartTrialMutation = /* GraphQL */ `
	mutation RestartManagedAccountTrial($accountId: ID!) {
		restartManagedAccountTrial(accountId: $accountId) {
			id
		}
	}
`;

export const useRestartManagedAccountTrial = () =>
	useMutation({
		mutationFn: (accountId: string) =>
			gqlReqNext({ query: restartTrialMutation, variables: { accountId } }),
	});

const deleteManagedAccountMutation = /* GraphQL */ `
	mutation DeleteManagedAccount($accountId: ID!) {
		deleteManagedAccount(accountId: $accountId)
	}
`;

export const useDeleteManagedAccount = () =>
	useMutation({
		mutationFn: (accountId: string) =>
			gqlReqNext({
				query: deleteManagedAccountMutation,
				variables: { accountId },
			}),
	});
